/**
 * Página `/finanzas-trabajador-admin` — panel admin/superadmin del ledger del
 * rol "trabajador" (empleado de Aloja Fácil SIN login: mantención, supervisor,
 * contador, etc.). Es el espejo del panel del aseador, pero:
 *
 *   - **Movimientos-only**: el trabajador no hace aseos → su ledger son solo
 *     movimientos (reembolsos de inventario que adelantó + adelantos / bonos /
 *     multas que registre el admin).
 *   - **Sin auto-vista**: el trabajador no tiene login (auth lo excluye), así
 *     que no existe `/finanzas-trabajador` — solo esta vista admin.
 *
 * Permite:
 *   1. **Global**: tabla de todos los trabajadores con neto / pagado /
 *      pendiente del rango. Click en una fila → detalle de ese trabajador.
 *   2. **Detalle**: KPIs + tabla de movimientos con el ciclo de aprobación
 *      (pendiente → pagado / denegado, reversible, editable, borrable solo si
 *      pendiente/denegado — regla D-D). Registrar movimiento manual + aprobar
 *      todos los pendientes del rango de un click.
 *
 * Backend: `GET/POST/PATCH/DELETE /api/finanzas/trabajador/:id/...` (admin-only).
 * Realtime: evento WS `TRABAJADOR_LEDGER_CHANGED` (solo admins) → refetch.
 * Cierra el pendiente "ciclo trabajador (tipo=4) trackeable" de
 * docs/pendientes-finanzas-estadisticas.md.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Button,
  DatePicker,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Radio,
  Segmented,
  Select,
  Space,
  Spin,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  CheckCircleOutlined,
  CheckSquareOutlined,
  CloseCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  SearchOutlined,
  SyncOutlined,
  UndoOutlined,
  UserOutlined,
} from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import Layout from "../../Components/Layout";
import PageHeader from "../../Components/PageHeader";
import { useSidebar } from "../../hooks/useSidebar";
import { useWebSocket } from "../../context/WebSocketContext";
import {
  finanzasTrabajadorApi,
  usersApi,
  propiedadesApi,
} from "../../services/api";
import type { Trabajador } from "../../services/users.api";
import type {
  ConceptoAseador,
  MovimientoAseador,
  MovimientoEstado,
} from "../../services/finanzas.api";
import { formatPropertyName } from "../../utils/propertyDisplay";
import { fuzzyIncludes } from "../../utils/fuzzyMatch";
import { ahoraChile } from "../../utils/fechaChile";
import ComprobantesAsignacion from "../finanzas-aseador/ComprobantesAsignacion";
import { cambiarEstadoAsignacionLavanderia } from "../../services/finanzasLavanderia.api";
import { useFiltroPersistido } from "../../hooks/useFiltroPersistido";
import {
  CADUCIDAD_FECHAS_DIAS,
  deserializarRango,
  serializarRango,
} from "../../hooks/filtrosPersistidos";

const { Title, Text } = Typography;

const fmtClp = (n: number): string => `$${Math.round(n).toLocaleString("es-CL")}`;
const fmtFecha = (s: string | null): string =>
  s ? dayjs(s).format("DD-MM-YYYY") : "—";

/** Conceptos válidos (mismo set que el aseador — el backend valida igual). */
const CONCEPTOS: {
  value: ConceptoAseador;
  label: string;
  hint: string;
  signo: 1 | -1;
}[] = [
  { value: "adelanto", label: "Adelanto", hint: "Dinero a cuenta", signo: 1 },
  { value: "bono_extra", label: "Bono extra", hint: "Reconocimiento puntual", signo: 1 },
  {
    value: "reembolso_gasto",
    label: "Reembolso de gasto",
    hint: "El trabajador adelantó plata por AF",
    signo: 1,
  },
  { value: "propina", label: "Propina", hint: "Tip de cliente/huésped", signo: 1 },
  {
    value: "descuento_disciplinario",
    label: "Descuento disciplinario",
    hint: "Resta por incumplimiento",
    signo: -1,
  },
  { value: "multa", label: "Multa", hint: "Cargo punitivo formal", signo: -1 },
  { value: "otro", label: "Otro", hint: "No categorizado", signo: 1 },
];

/** Estilo del tag de estado del movimiento. */
const ESTADO_TAG: Record<MovimientoEstado, { color: string; label: string }> = {
  pendiente: { color: "gold", label: "Pendiente" },
  pagado: { color: "green", label: "Aprobado" },
  denegado: { color: "red", label: "Denegado" },
};

/** Fila de la tabla "Global" (un trabajador, totales del rango). */
interface ResumenTrabajadorRow {
  id: number;
  nombre: string;
  cantidadMovimientos: number;
  cantidadPendientes: number;
  neto: number;
  pagado: number;
  pendiente: number;
  sinEstado: number;
}

type EstadoFiltro = "all" | "pendiente" | "pagado" | "denegado";

export default function FinanzasTrabajadorAdmin() {
  const { sidebarOpen, toggleSidebar } = useSidebar();
  const { socket } = useWebSocket();

  // Lista de trabajadores (para selector + tabla global).
  const [trabajadores, setTrabajadores] = useState<Trabajador[]>([]);
  const [loadingList, setLoadingList] = useState(true);

  // Vista + rango. Round 2026-09-24: se recuerdan por usuario; el rango de
  // fechas caduca a los 7 días porque este panel aprueba pagos y un rango
  // viejo restaurado cambia en silencio qué se aprueba en bloque.
  const [vista, setVista] = useFiltroPersistido<"global" | "detalle">(
    "finanzasTrabajadorAdmin.vista",
    "global",
    { validar: (v) => v === "global" || v === "detalle" },
  );
  const [rangeMode, setRangeMode] = useFiltroPersistido<"meses" | "rango">(
    "finanzasTrabajadorAdmin.modoRango",
    "meses",
    { validar: (v) => v === "meses" || v === "rango" },
  );
  const [months, setMonths] = useFiltroPersistido<number>(
    "finanzasTrabajadorAdmin.meses",
    6,
    { validar: (v) => v === 3 || v === 6 || v === 12 },
  );
  const [customRange, setCustomRange] = useFiltroPersistido<
    [Dayjs, Dayjs] | null
  >("finanzasTrabajadorAdmin.rango", null, {
    caducaEnDias: CADUCIDAD_FECHAS_DIAS,
    serializar: serializarRango,
    deserializar: (crudo) => {
      const rango = deserializarRango(crudo);
      if (rango === undefined) return undefined;
      // Media fecha no sirve: `buildRangeOpts` exige el par completo.
      if (!rango || !rango[0] || !rango[1]) return null;
      return [rango[0], rango[1]] as [Dayjs, Dayjs];
    },
  });

  // Detalle de un trabajador.
  const [trabajadorId, setTrabajadorId] = useFiltroPersistido<number | null>(
    "finanzasTrabajadorAdmin.trabajador",
    null,
    { validar: (v) => v === null || (typeof v === "number" && v > 0) },
  );
  const [trabajadorNombre, setTrabajadorNombre] = useState<string>("");
  const [movimientos, setMovimientos] = useState<MovimientoAseador[]>([]);
  const [totales, setTotales] = useState({
    neto: 0,
    pagado: 0,
    pendiente: 0,
    sinEstado: 0,
    cantidadPendientes: 0,
  });
  const [loadingDetalle, setLoadingDetalle] = useState(false);
  const [filtroEstado, setFiltroEstado] = useFiltroPersistido<EstadoFiltro>(
    "finanzasTrabajadorAdmin.estado",
    "all",
    {
      validar: (v) =>
        v === "all" || v === "pendiente" || v === "pagado" || v === "denegado",
    },
  );

  // Vista global.
  const [resumenes, setResumenes] = useState<ResumenTrabajadorRow[]>([]);
  const [loadingGlobal, setLoadingGlobal] = useState(false);
  const [busqueda, setBusqueda] = useFiltroPersistido(
    "finanzasTrabajadorAdmin.busqueda",
    "",
    { validar: (v) => typeof v === "string" },
  );

  // Modales / acciones.
  const [registrarOpen, setRegistrarOpen] = useState(false);
  const [editing, setEditing] = useState<MovimientoAseador | null>(null);
  const [bulkLoading, setBulkLoading] = useState(false);

  /** Opciones de rango para el backend (precedencia: rango custom > meses). */
  const buildRangeOpts = useCallback(():
    | { months: number }
    | { range: { from: string; to: string; label: string } } => {
    if (rangeMode === "rango" && customRange) {
      const [from, to] = customRange;
      return {
        range: {
          from: from.format("YYYY-MM-DD"),
          to: to.format("YYYY-MM-DD"),
          label: `${from.format("DD-MM-YYYY")} → ${to.format("DD-MM-YYYY")}`,
        },
      };
    }
    return { months };
  }, [rangeMode, customRange, months]);

  // ─── Carga de datos ───────────────────────────────────────────────────
  const loadList = useCallback(async () => {
    setLoadingList(true);
    const list = await usersApi.getTrabajadores();
    setTrabajadores(Array.isArray(list) ? list : []);
    setLoadingList(false);
  }, []);

  const loadDetalle = useCallback(
    async (id: number) => {
      setLoadingDetalle(true);
      const resp = await finanzasTrabajadorApi.getFinanzasTrabajadorById(
        id,
        buildRangeOpts(),
      );
      if (!resp) {
        setMovimientos([]);
        setTotales({ neto: 0, pagado: 0, pendiente: 0, sinEstado: 0, cantidadPendientes: 0 });
      } else {
        const meses = resp.data.meses ?? [];
        // El trabajador no tiene ciclo semanal de pago: aplanamos todos los
        // movimientos del rango en una sola tabla ordenada por fecha desc.
        const movs = meses
          .flatMap((m) => m.movimientos)
          .sort((a, b) => String(b.fecha ?? "").localeCompare(String(a.fecha ?? "")));
        setMovimientos(movs);
        setTotales({
          neto: meses.reduce((s, m) => s + m.totalGeneralNeto, 0),
          pagado: meses.reduce((s, m) => s + m.totalGeneralPagado, 0),
          pendiente: meses.reduce((s, m) => s + m.totalGeneralPendiente, 0),
          sinEstado: meses.reduce((s, m) => s + (m.totalMovimientosSinEstado || 0), 0),
          cantidadPendientes: meses.reduce((s, m) => s + m.cantidadPendientes, 0),
        });
        setTrabajadorNombre(resp.data.trabajador?.nombre ?? "");
      }
      setLoadingDetalle(false);
    },
    [buildRangeOpts],
  );

  const loadGlobal = useCallback(
    async (list: Trabajador[]) => {
      if (list.length === 0) {
        setResumenes([]);
        return;
      }
      setLoadingGlobal(true);
      const opts = buildRangeOpts();
      const results = await Promise.allSettled(
        list.map((t) =>
          finanzasTrabajadorApi
            .getFinanzasTrabajadorById(t.id, opts)
            .then((resp) => ({ t, resp })),
        ),
      );
      const filas: ResumenTrabajadorRow[] = results.map((r, i) => {
        const base: ResumenTrabajadorRow = {
          id: list[i].id,
          nombre: list[i].nombre,
          cantidadMovimientos: 0,
          cantidadPendientes: 0,
          neto: 0,
          pagado: 0,
          pendiente: 0,
          sinEstado: 0,
        };
        if (r.status !== "fulfilled" || !r.value.resp) return base;
        const meses = r.value.resp.data.meses ?? [];
        for (const m of meses) {
          base.cantidadMovimientos += m.cantidadMovimientos;
          base.cantidadPendientes += m.cantidadPendientes;
          base.neto += m.totalGeneralNeto;
          base.pagado += m.totalGeneralPagado;
          base.pendiente += m.totalGeneralPendiente;
          base.sinEstado += m.totalMovimientosSinEstado || 0;
        }
        return base;
      });
      // Primero los que tienen pendientes, luego por neto desc.
      filas.sort(
        (a, b) =>
          b.cantidadPendientes - a.cantidadPendientes || b.neto - a.neto,
      );
      setResumenes(filas);
      setLoadingGlobal(false);
    },
    [buildRangeOpts],
  );

  // Carga inicial de la lista.
  useEffect(() => {
    void loadList();
  }, [loadList]);

  // El trabajador recordado puede haberse eliminado desde /trabajadores (rol
  // sin login, se borra fácil). Sin esto el Select queda sin label y el
  // detalle carga vacío, sin decir por qué. Solo corre con la lista ya
  // cargada y no vacía: si el fetch falló, no hay con qué comparar.
  useEffect(() => {
    if (loadingList || trabajadores.length === 0 || trabajadorId == null) {
      return;
    }
    if (!trabajadores.some((t) => t.id === trabajadorId)) {
      setTrabajadorId(null);
    }
  }, [loadingList, trabajadores, trabajadorId, setTrabajadorId]);

  // Recarga la vista activa cuando cambia el rango o la selección.
  useEffect(() => {
    if (vista === "global") {
      void loadGlobal(trabajadores);
    } else if (vista === "detalle" && trabajadorId) {
      void loadDetalle(trabajadorId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vista, trabajadorId, trabajadores, months, rangeMode, customRange]);

  // Realtime: cualquier cambio en un ledger de trabajador → refetch.
  useEffect(() => {
    if (!socket) return;
    const handler = (ev: MessageEvent) => {
      try {
        const msg = JSON.parse(ev.data);
        if (msg?.type !== "TRABAJADOR_LEDGER_CHANGED") return;
        if (vista === "detalle" && trabajadorId) {
          void loadDetalle(trabajadorId);
        } else if (vista === "global") {
          void loadGlobal(trabajadores);
        }
      } catch {
        /* ignore */
      }
    };
    socket.addEventListener("message", handler);
    return () => socket.removeEventListener("message", handler);
  }, [socket, vista, trabajadorId, trabajadores, loadDetalle, loadGlobal]);

  // ─── Acciones admin ───────────────────────────────────────────────────
  const reloadActiva = useCallback(() => {
    if (vista === "detalle" && trabajadorId) void loadDetalle(trabajadorId);
    else void loadGlobal(trabajadores);
  }, [vista, trabajadorId, trabajadores, loadDetalle, loadGlobal]);

  // Las transiciones aceptan el movimiento completo, no solo su id: una
  // ocurrencia de flujo REGULAR no tiene fila propia (el regular es UNA fila
  // que vale por N meses), así que su estado se cambia en el endpoint de
  // regulares indicando (gastoId, período). Round 2026-07-27.
  const transicionar = async (m: MovimientoAseador, estado: MovimientoEstado) => {
    if (!trabajadorId) return;
    if (m.origen === "lavanderia_asignacion") {
      if (!m.asignacionId || !m.revision || estado === "denegado") return;
      await cambiarEstadoAsignacionLavanderia(m.asignacionId, m.revision, estado);
    } else if (m.esRegular && m.periodo) {
      await finanzasTrabajadorApi.marcarPagoRegularTrabajador(
        trabajadorId,
        m.id,
        m.periodo,
        estado,
      );
    } else if (estado === "pagado") {
      await finanzasTrabajadorApi.aprobarMovimientoTrabajador(trabajadorId, m.id);
    } else if (estado === "denegado") {
      await finanzasTrabajadorApi.denegarMovimientoTrabajador(trabajadorId, m.id);
    } else {
      await finanzasTrabajadorApi.revertirMovimientoTrabajador(trabajadorId, m.id);
    }
    reloadActiva();
  };

  const onAprobar = (m: MovimientoAseador) => transicionar(m, "pagado");
  const onDenegar = (m: MovimientoAseador) => transicionar(m, "denegado");
  const onRevertir = (m: MovimientoAseador) => transicionar(m, "pendiente");
  const onBorrar = async (movId: number) => {
    if (!trabajadorId) return;
    await finanzasTrabajadorApi.borrarMovimientoTrabajador(trabajadorId, movId);
    reloadActiva();
  };

  const onAprobarPendientesRango = async (idObjetivo: number) => {
    const opts = buildRangeOpts();
    // El bulk necesita un rango explícito. Si estamos en modo "meses",
    // resolvemos el rango pidiendo el resumen (su `data.rango` es el rango real).
    let from: string;
    let to: string;
    if ("range" in opts) {
      from = opts.range.from;
      to = opts.range.to;
    } else {
      const resp = await finanzasTrabajadorApi.getFinanzasTrabajadorById(
        idObjetivo,
        opts,
      );
      if (!resp) return;
      from = resp.data.rango.from;
      to = resp.data.rango.to;
    }
    setBulkLoading(true);
    await finanzasTrabajadorApi.aprobarPendientesBulkTrabajador(idObjetivo, {
      from,
      to,
    });
    setBulkLoading(false);
    reloadActiva();
  };

  const movimientosFiltrados = useMemo(() => {
    if (filtroEstado === "all") return movimientos;
    return movimientos.filter((m) => m.estado === filtroEstado);
  }, [movimientos, filtroEstado]);

  // fuzzyIncludes pliega tildes ("sebastian" encuentra "Sebastián").
  const resumenesFiltrados = useMemo(() => {
    const q = busqueda.trim();
    if (!q) return resumenes;
    return resumenes.filter((r) => fuzzyIncludes(r.nombre, q));
  }, [resumenes, busqueda]);

  // ─── Columnas tabla global ─────────────────────────────────────────────
  const columnsGlobal: ColumnsType<ResumenTrabajadorRow> = [
    {
      title: "Trabajador",
      dataIndex: "nombre",
      key: "nombre",
      render: (nombre: string) => (
        <Space>
          <Avatar size="small" icon={<UserOutlined />} />
          <strong>{nombre}</strong>
        </Space>
      ),
      sorter: (a, b) => a.nombre.localeCompare(b.nombre),
    },
    {
      title: "Movimientos",
      dataIndex: "cantidadMovimientos",
      key: "cantidadMovimientos",
      align: "center",
      render: (n: number) => <Tag color="blue">{n}</Tag>,
      sorter: (a, b) => a.cantidadMovimientos - b.cantidadMovimientos,
    },
    {
      title: "Pendientes",
      dataIndex: "cantidadPendientes",
      key: "cantidadPendientes",
      align: "center",
      render: (n: number) =>
        n > 0 ? <Tag color="gold">{n}</Tag> : <Text type="secondary">0</Text>,
      sorter: (a, b) => a.cantidadPendientes - b.cantidadPendientes,
    },
    {
      title: "Neto del rango",
      dataIndex: "neto",
      key: "neto",
      align: "right",
      render: (n: number) => (
        <strong style={{ color: n >= 0 ? "#16a34a" : "#dc2626" }}>
          {n >= 0 ? "+" : ""}
          {fmtClp(n)}
        </strong>
      ),
      sorter: (a, b) => a.neto - b.neto,
    },
    {
      title: "Pagado",
      dataIndex: "pagado",
      key: "pagado",
      align: "right",
      render: (n: number) => <span style={{ color: "#16a34a" }}>{fmtClp(n)}</span>,
    },
    {
      title: "Pendiente",
      dataIndex: "pendiente",
      key: "pendiente",
      align: "right",
      render: (n: number) => (
        <span style={{ color: "#d97706" }}>{fmtClp(n)}</span>
      ),
    },
    {
      title: "Sin estado",
      dataIndex: "sinEstado",
      key: "sinEstado",
      align: "right",
      render: (n: number) => <Tooltip title="Incluido en el neto; aún no se indicó si está pendiente o pagado.">{fmtClp(n)}</Tooltip>,
    },
    {
      title: "Acciones",
      key: "acciones",
      align: "center",
      render: (_: unknown, row: ResumenTrabajadorRow) =>
        row.cantidadPendientes > 0 ? (
          <Popconfirm
            title="Aprobar movimientos generales pendientes"
            description="Solo procesa movimientos generales del rango. Los pagos de lavandería se aprueban individualmente en el detalle."
            okText="Aprobar"
            cancelText="Cancelar"
            onConfirm={(e) => {
              e?.stopPropagation();
              void onAprobarPendientesRango(row.id);
            }}
            onCancel={(e) => e?.stopPropagation()}
          >
            <Button
              size="small"
              icon={<CheckSquareOutlined />}
              loading={bulkLoading}
              onClick={(e) => e.stopPropagation()}
            >
              Aprobar generales
            </Button>
          </Popconfirm>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
  ];

  // ─── Columnas tabla de movimientos (detalle) ───────────────────────────
  const columnsMov: ColumnsType<MovimientoAseador> = [
    {
      title: "Fecha",
      dataIndex: "fecha",
      key: "fecha",
      width: 110,
      render: (f: string | null) => fmtFecha(f),
    },
    {
      title: "Concepto",
      key: "concepto",
      render: (_: unknown, m: MovimientoAseador) => (
        <Space direction="vertical" size={0}>
          <Space size={4} wrap>
            <Tag color={m.signo === 1 ? "blue" : "red"}>
              {m.conceptoLabel || m.conceptoCodigo || "Movimiento"}
            </Tag>
            {m.esRegular && (
              <Tooltip title="Pago recurrente: se repite todos los meses mientras el flujo esté vigente. Se edita en Flujos, no acá.">
                <Tag color="purple" icon={<SyncOutlined />}>
                  Regular
                </Tag>
              </Tooltip>
            )}
          </Space>
          {m.descripcion && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {m.descripcion}
            </Text>
          )}
          {m.notas && <Text type="secondary" style={{ fontSize: 12 }}>Notas: {m.notas}</Text>}
          <ComprobantesAsignacion movimiento={m} />
        </Space>
      ),
    },
    {
      title: "Contraparte",
      key: "contraparte",
      width: 200,
      render: (_: unknown, m: MovimientoAseador) => (
        <Space direction="vertical" size={0}>
          <Text>
            {m.signo === 1 ? "Recibe de " : "Paga a "}
            <strong>{m.contraparte?.nombre || "—"}</strong>
          </Text>
          {m.rolAf === "intermediario" && <Tag>Intermediario</Tag>}
        </Space>
      ),
    },
    {
      title: "Estado",
      dataIndex: "estado",
      key: "estado",
      width: 120,
      render: (estado: MovimientoEstado | "sin_definir") => {
        if (estado === "sin_definir") return <Tag>Sin indicar</Tag>;
        const t = ESTADO_TAG[estado] ?? ESTADO_TAG.pagado;
        return <Tag color={t.color}>{t.label}</Tag>;
      },
    },
    {
      title: "Monto",
      dataIndex: "monto",
      key: "monto",
      width: 130,
      align: "right",
      render: (_: unknown, m: MovimientoAseador) => (
        <strong style={{ color: m.signo === 1 ? "#16a34a" : "#dc2626" }}>
          {m.signo === 1 ? "+" : "−"}
          {fmtClp(m.monto)}
        </strong>
      ),
    },
    {
      title: "Acciones",
      key: "acciones",
      width: 290,
      render: (_: unknown, m: MovimientoAseador) => {
        if (m.origen === "lavanderia_asignacion") return <Space wrap>
          {m.estado !== "pagado" && <Button size="small" type="primary" onClick={() => void onAprobar(m)}>Marcar pagado</Button>}
          {m.estado !== "pendiente" && <Button size="small" onClick={() => void onRevertir(m)}>Marcar pendiente</Button>}
          <Button size="small" href="/lavanderia" target="_blank">Ver lavandería</Button>
        </Space>;
        const esPendiente = m.estado === "pendiente";
        const esDenegado = m.estado === "denegado";
        const esPagado = m.estado === "pagado";
        // Una ocurrencia de flujo regular se aprueba/deniega mes a mes, pero
        // NO se edita ni se borra desde acá: no es una fila propia, es la
        // proyección de un flujo que vive en /flujos. Editarla acá cambiaría
        // todos los meses; borrarla no tendría a qué apuntar.
        const esOcurrenciaRegular = Boolean(m.esRegular);
        return (
          <Space wrap>
            {esPendiente && (
              <>
                <Button
                  size="small"
                  type="primary"
                  icon={<CheckCircleOutlined />}
                  onClick={() => void onAprobar(m)}
                >
                  {esOcurrenciaRegular ? "Marcar pagado" : "Aprobar"}
                </Button>
                <Popconfirm
                  title={
                    esOcurrenciaRegular
                      ? "¿Marcar este mes como no pagado?"
                      : "¿Denegar este movimiento?"
                  }
                  okText="Denegar"
                  cancelText="Cancelar"
                  onConfirm={() => void onDenegar(m)}
                >
                  <Button size="small" danger icon={<CloseCircleOutlined />}>
                    Denegar
                  </Button>
                </Popconfirm>
              </>
            )}
            {(esPagado || esDenegado) && (
              <Popconfirm
                title="Revertir a pendiente"
                okText="Revertir"
                cancelText="Cancelar"
                onConfirm={() => void onRevertir(m)}
              >
                <Button size="small" danger icon={<UndoOutlined />}>
                  {esPagado ? "Anular pago" : "Anular"}
                </Button>
              </Popconfirm>
            )}
            {esOcurrenciaRegular ? (
              <Tooltip title="Este pago se repite todos los meses. Su monto, su día y hasta cuándo rige se editan en Flujos.">
                <Button size="small" href="/flujos" target="_blank">
                  Ver en Flujos
                </Button>
              </Tooltip>
            ) : (
              <>
                <Button
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => setEditing(m)}
                >
                  Editar
                </Button>
                {(esPendiente || esDenegado) && (
                  <Popconfirm
                    title="¿Borrar este movimiento?"
                    okText="Borrar"
                    cancelText="Cancelar"
                    onConfirm={() => void onBorrar(m.id)}
                  >
                    <Button size="small" danger icon={<DeleteOutlined />}>
                      Borrar
                    </Button>
                  </Popconfirm>
                )}
              </>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <Layout sidebarOpen={sidebarOpen} toggleSidebar={toggleSidebar}>
      <div className="fa-page" style={{ padding: 16 }}>
        <PageHeader
          title="Finanzas del trabajador"
          helpText="Ledger de los empleados sin login (mantención, supervisor, contador, etc.). Reembolsos de inventario que adelantaron + movimientos manuales (adelantos, bonos, multas). Mismo ciclo de aprobación que el aseador: pendiente → aprobado / denegado, reversible. Un movimiento pagado se corrige con 'Anular pago', no se borra."
        />

        {/* Toolbar: vista + rango */}
        <Space wrap style={{ marginBottom: 12 }}>
          <Segmented
            value={vista}
            onChange={(v) => setVista(v as "global" | "detalle")}
            options={[
              { label: "Global", value: "global" },
              { label: "Por trabajador", value: "detalle" },
            ]}
          />
          <Segmented
            value={rangeMode}
            onChange={(v) => setRangeMode(v as "meses" | "rango")}
            options={[
              { label: "Meses", value: "meses" },
              { label: "Rango", value: "rango" },
            ]}
          />
          {rangeMode === "meses" ? (
            <Segmented
              value={months}
              onChange={(v) => setMonths(Number(v))}
              options={[
                { label: "3 meses", value: 3 },
                { label: "6 meses", value: 6 },
                { label: "12 meses", value: 12 },
              ]}
            />
          ) : (
            <DatePicker.RangePicker
              value={customRange}
              onChange={(v) =>
                setCustomRange(v && v[0] && v[1] ? [v[0], v[1]] : null)
              }
              format="DD-MM-YYYY"
              allowClear
            />
          )}
          {vista === "detalle" && (
            <Select
              showSearch
              placeholder="Selecciona un trabajador"
              style={{ minWidth: 240 }}
              loading={loadingList}
              value={trabajadorId ?? undefined}
              optionFilterProp="label"
              onChange={(v) => setTrabajadorId(Number(v))}
              options={trabajadores.map((t) => ({ value: t.id, label: t.nombre }))}
            />
          )}
        </Space>

        {/* ─── Vista GLOBAL ─── */}
        {vista === "global" && (
          <>
            <Space style={{ marginBottom: 12 }}>
              <Input
                allowClear
                prefix={<SearchOutlined />}
                placeholder="Buscar trabajador"
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                style={{ width: 260 }}
              />
            </Space>
            {loadingList || loadingGlobal ? (
              <Spin />
            ) : (
              <Table<ResumenTrabajadorRow>
                rowKey="id"
                size="small"
                columns={columnsGlobal}
                dataSource={resumenesFiltrados}
                pagination={false}
                locale={{
                  emptyText: (
                    <Empty description="No hay trabajadores registrados" />
                  ),
                }}
                onRow={(row) => ({
                  onClick: () => {
                    setTrabajadorId(row.id);
                    setVista("detalle");
                  },
                  style: { cursor: "pointer" },
                })}
              />
            )}
          </>
        )}

        {/* ─── Vista DETALLE ─── */}
        {vista === "detalle" && (
          <>
            {!trabajadorId ? (
              <Alert
                type="info"
                showIcon
                message="Selecciona un trabajador para ver su ledger."
              />
            ) : loadingDetalle ? (
              <Spin />
            ) : (
              <>
                <Title level={4} style={{ marginTop: 0 }}>
                  {trabajadorNombre || `Trabajador #${trabajadorId}`}
                </Title>

                <Space wrap size="large" style={{ marginBottom: 12 }}>
                  <Statistic
                    title="Neto del rango"
                    value={totales.neto}
                    formatter={(v) => fmtClp(Number(v))}
                    valueStyle={{
                      color: totales.neto >= 0 ? "#16a34a" : "#dc2626",
                    }}
                  />
                  <Statistic
                    title="Pagado"
                    value={totales.pagado}
                    formatter={(v) => fmtClp(Number(v))}
                    valueStyle={{ color: "#16a34a" }}
                  />
                  <Statistic
                    title="Pendiente"
                    value={totales.pendiente}
                    formatter={(v) => fmtClp(Number(v))}
                    valueStyle={{ color: "#d97706" }}
                  />
                  <Statistic title={<Tooltip title="Incluido en el neto; aún no se indicó si está pendiente o pagado.">Sin estado</Tooltip>} value={totales.sinEstado} formatter={(v) => fmtClp(Number(v))} />
                </Space>

                <Space wrap style={{ marginBottom: 12 }}>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => setRegistrarOpen(true)}
                  >
                    Registrar movimiento
                  </Button>
                  {totales.cantidadPendientes > 0 && (
                    <Popconfirm
                      title="Aprobar movimientos generales pendientes"
                      description="Solo procesa movimientos generales del rango. Los pagos de lavandería se aprueban individualmente en el detalle."
                      okText="Aprobar"
                      cancelText="Cancelar"
                      onConfirm={() =>
                        void onAprobarPendientesRango(trabajadorId)
                      }
                    >
                      <Button icon={<CheckSquareOutlined />} loading={bulkLoading}>
                        Aprobar generales
                      </Button>
                    </Popconfirm>
                  )}
                  <Segmented
                    value={filtroEstado}
                    onChange={(v) => setFiltroEstado(v as EstadoFiltro)}
                    options={[
                      { label: "Todos", value: "all" },
                      { label: "Pendientes", value: "pendiente" },
                      { label: "Pagados", value: "pagado" },
                      { label: "Denegados", value: "denegado" },
                    ]}
                  />
                </Space>

                <Table<MovimientoAseador>
                  // El `id` de una ocurrencia de flujo regular se repite entre
                  // meses (es el id del flujo, no de la fila del mes), así que
                  // la clave incluye el período. Sin esto React colapsaría
                  // todos los meses del mismo regular en una sola fila.
                  rowKey={(m) =>
                    m.origen === "lavanderia_asignacion" ? `lavanderia-${m.asignacionId}` : m.esRegular && m.periodo ? `reg-${m.id}-${m.periodo}` : String(m.id)
                  }
                  size="small"
                  columns={columnsMov}
                  dataSource={movimientosFiltrados}
                  pagination={false}
                  locale={{
                    emptyText: (
                      <Empty description="Sin movimientos en este rango" />
                    ),
                  }}
                />
              </>
            )}
          </>
        )}
      </div>

      {/* Modal registrar */}
      {registrarOpen && trabajadorId && (
        <RegistrarMovimientoTrabajadorModal
          trabajadorId={trabajadorId}
          trabajadorNombre={trabajadorNombre}
          onClose={() => setRegistrarOpen(false)}
          onCreated={() => {
            setRegistrarOpen(false);
            reloadActiva();
          }}
        />
      )}

      {/* Modal editar */}
      {editing && trabajadorId && (
        <EditarMovimientoTrabajadorModal
          trabajadorId={trabajadorId}
          movimiento={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            reloadActiva();
          }}
        />
      )}
    </Layout>
  );
}

// ─── Modal: registrar movimiento ──────────────────────────────────────────
interface RegistrarProps {
  trabajadorId: number;
  trabajadorNombre: string;
  onClose: () => void;
  onCreated: () => void;
}

function RegistrarMovimientoTrabajadorModal({
  trabajadorId,
  trabajadorNombre,
  onClose,
  onCreated,
}: RegistrarProps) {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [contraparteKind, setContraparteKind] = useState<
    "af" | "propiedad" | "externo"
  >("af");
  const [propiedades, setPropiedades] = useState<
    { id: number; nombrepropiedad: string }[]
  >([]);

  // Carga las propiedades solo cuando se elige contraparte=propiedad.
  useEffect(() => {
    if (contraparteKind !== "propiedad" || propiedades.length > 0) return;
    void propiedadesApi.getNombres().then((resp) => {
      if (resp?.data) setPropiedades(resp.data);
    });
  }, [contraparteKind, propiedades.length]);

  const onConcepto = (value: ConceptoAseador) => {
    const c = CONCEPTOS.find((x) => x.value === value);
    if (c) form.setFieldsValue({ signo: c.signo });
  };

  const onOk = async () => {
    const values = await form.validateFields();
    setSaving(true);
    let contraparte:
      | { kind: "af" }
      | { kind: "propiedad"; propiedadId: number }
      | { kind: "externo"; nombreExterno?: string };
    if (contraparteKind === "propiedad") {
      contraparte = { kind: "propiedad", propiedadId: Number(values.propiedadId) };
    } else if (contraparteKind === "externo") {
      contraparte = { kind: "externo", nombreExterno: values.nombreExterno };
    } else {
      contraparte = { kind: "af" };
    }
    const res = await finanzasTrabajadorApi.crearMovimientoTrabajador(
      trabajadorId,
      {
        monto: Math.round(Number(values.monto)),
        signo: values.signo,
        concepto: values.concepto,
        descripcion: values.descripcion || "",
        fecha: (values.fecha as Dayjs).format("YYYY-MM-DD"),
        contraparte,
      },
    );
    setSaving(false);
    if (res) onCreated();
  };

  return (
    <Modal
      open
      title={`Registrar movimiento — ${trabajadorNombre}`}
      onCancel={onClose}
      onOk={onOk}
      confirmLoading={saving}
      okText="Registrar"
      cancelText="Cancelar"
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          signo: 1,
          concepto: "reembolso_gasto",
          fecha: ahoraChile(),
        }}
      >
        <Form.Item
          name="concepto"
          label="Concepto"
          rules={[{ required: true }]}
        >
          <Select
            onChange={(v) => onConcepto(v as ConceptoAseador)}
            options={CONCEPTOS.map((c) => ({
              value: c.value,
              label: `${c.label} — ${c.hint}`,
            }))}
          />
        </Form.Item>

        <Form.Item name="signo" label="Dirección del flujo" rules={[{ required: true }]}>
          <Radio.Group
            options={[
              { label: "El trabajador recibe (+)", value: 1 },
              { label: "El trabajador paga (−)", value: -1 },
            ]}
            optionType="button"
          />
        </Form.Item>

        <Form.Item
          name="monto"
          label="Monto"
          rules={[{ required: true, message: "Ingresa un monto" }]}
        >
          <InputNumber<number>
            min={1}
            step={1000}
            style={{ width: "100%" }}
            formatter={(v) => `$ ${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ".")}
            parser={(v) => Number((v || "").replace(/[^\d]/g, ""))}
          />
        </Form.Item>

        <Form.Item name="fecha" label="Fecha" rules={[{ required: true }]}>
          <DatePicker format="DD-MM-YYYY" style={{ width: "100%" }} />
        </Form.Item>

        <Form.Item label="Contraparte">
          <Radio.Group
            value={contraparteKind}
            onChange={(e) => setContraparteKind(e.target.value)}
            options={[
              { label: "Aloja Fácil", value: "af" },
              { label: "Propiedad / Cliente", value: "propiedad" },
              { label: "Externo", value: "externo" },
            ]}
            optionType="button"
          />
        </Form.Item>

        {contraparteKind === "propiedad" && (
          <Form.Item
            name="propiedadId"
            label="Propiedad"
            rules={[{ required: true, message: "Elige una propiedad" }]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              placeholder="Propiedad"
              options={propiedades.map((p) => ({
                value: p.id,
                label: formatPropertyName(p.nombrepropiedad),
              }))}
            />
          </Form.Item>
        )}

        {contraparteKind === "externo" && (
          <Form.Item name="nombreExterno" label="Nombre del externo">
            <Input placeholder="Ej. Ferretería, caja chica" maxLength={120} />
          </Form.Item>
        )}

        <Form.Item name="descripcion" label="Descripción">
          <Input.TextArea rows={2} maxLength={1000} />
        </Form.Item>

        <Text type="secondary" style={{ fontSize: 12 }}>
          El movimiento nace <strong>pendiente</strong> de aprobación. Con
          contraparte distinta de Aloja Fácil, AF solo intermedia (no afecta su
          utilidad).
        </Text>
      </Form>
    </Modal>
  );
}

// ─── Modal: editar movimiento ───────────────────────────────────────────────
interface EditarProps {
  trabajadorId: number;
  movimiento: MovimientoAseador;
  onClose: () => void;
  onSaved: () => void;
}

function EditarMovimientoTrabajadorModal({
  trabajadorId,
  movimiento,
  onClose,
  onSaved,
}: EditarProps) {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  const onOk = async () => {
    const values = await form.validateFields();
    setSaving(true);
    const patch: {
      monto?: number;
      concepto?: string;
      descripcion?: string;
      fecha?: string;
      signo?: 1 | -1;
    } = {
      monto: Math.round(Number(values.monto)),
      concepto: values.concepto,
      descripcion: values.descripcion || "",
      fecha: (values.fecha as Dayjs).format("YYYY-MM-DD"),
    };
    // Solo enviamos `signo` si cambió (evita re-derivar la dirección en balde).
    if (values.signo !== movimiento.signo) patch.signo = values.signo;
    const res = await finanzasTrabajadorApi.editarMovimientoTrabajador(
      trabajadorId,
      movimiento.id,
      patch,
    );
    setSaving(false);
    if (res) onSaved();
  };

  return (
    <Modal
      open
      title="Editar movimiento"
      onCancel={onClose}
      onOk={onOk}
      confirmLoading={saving}
      okText="Guardar"
      cancelText="Cancelar"
    >
      {movimiento.estado !== "pendiente" && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="Este movimiento estaba aprobado/denegado. Editarlo lo vuelve a 'pendiente' (requiere re-aprobación)."
        />
      )}
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          monto: movimiento.monto,
          signo: movimiento.signo,
          concepto: movimiento.conceptoCodigo || "otro",
          descripcion: movimiento.descripcion || "",
          fecha: movimiento.fecha ? dayjs(movimiento.fecha) : ahoraChile(),
        }}
      >
        <Form.Item name="monto" label="Monto" rules={[{ required: true }]}>
          <InputNumber<number>
            min={0}
            step={1000}
            style={{ width: "100%" }}
            formatter={(v) => `$ ${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ".")}
            parser={(v) => Number((v || "").replace(/[^\d]/g, ""))}
          />
        </Form.Item>
        <Form.Item name="signo" label="Dirección del flujo">
          <Radio.Group
            options={[
              { label: "El trabajador recibe (+)", value: 1 },
              { label: "El trabajador paga (−)", value: -1 },
            ]}
            optionType="button"
          />
        </Form.Item>
        <Form.Item name="concepto" label="Concepto">
          <Input maxLength={60} />
        </Form.Item>
        <Form.Item name="fecha" label="Fecha" rules={[{ required: true }]}>
          <DatePicker format="DD-MM-YYYY" style={{ width: "100%" }} />
        </Form.Item>
        <Form.Item name="descripcion" label="Descripción">
          <Input.TextArea rows={2} maxLength={1000} />
        </Form.Item>
      </Form>
    </Modal>
  );
}
